/**
 * Webhook Status Indicator Component
 * Displays webhook status with appropriate colors, icons, and tooltips
 */

import { useState } from 'react';
import { RefreshCwIcon } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { GitlabWebhook } from '@/lib/types/gitlab';

import { getStatusConfig } from './config';
import { BadgeContent } from './BadgeContent';

export type WebhookStatusIndicatorProps = {
  /** Webhook data (null if no webhook exists) */
  webhook: GitlabWebhook | null;
  /** Whether the status is being refreshed */
  isRefreshing?: boolean;
  /** Callback to refresh webhook status */
  onRefresh?: () => void;
  /** Show detailed tooltip information */
  showDetails?: boolean;
  /** Size variant */
  size?: 'sm' | 'md';
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleString();
};

const SimpleIndicator = ({
  statusConfig,
  isRefreshing,
  onRefresh,
  size = 'md',
}: {
  statusConfig: ReturnType<typeof getStatusConfig>;
  isRefreshing: boolean;
  onRefresh?: () => void;
  size?: 'sm' | 'md';
}): JSX.Element => (
  <div className="flex items-center gap-2">
    <Badge variant={statusConfig.color}>
      <BadgeContent
        isRefreshing={isRefreshing}
        IconComponent={statusConfig.icon}
        label={statusConfig.label}
        size={size}
      />
    </Badge>
    {onRefresh && (
      <Button
        variant="ghost"
        size="sm"
        onClick={onRefresh}
        disabled={isRefreshing}
        className="h-6 w-6 p-0"
      >
        <RefreshCwIcon
          className={`h-3 w-3 ${isRefreshing ? 'animate-spin' : ''}`}
        />
      </Button>
    )}
  </div>
);

const DetailedIndicator = ({
  webhook,
  statusConfig,
  isRefreshing,
  onRefresh,
  size = 'md',
}: {
  webhook: GitlabWebhook;
  statusConfig: ReturnType<typeof getStatusConfig>;
  isRefreshing: boolean;
  onRefresh?: () => void;
  size?: 'sm' | 'md';
}): JSX.Element => {
  const [tooltipOpen, setTooltipOpen] = useState(false);

  return (
    <TooltipProvider>
      <Tooltip open={tooltipOpen} onOpenChange={setTooltipOpen}>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-2">
            <Badge variant={statusConfig.color} className="cursor-help">
              <BadgeContent
                isRefreshing={isRefreshing}
                IconComponent={statusConfig.icon}
                label={statusConfig.label}
                size={size}
              />
            </Badge>
            {onRefresh && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onRefresh}
                disabled={isRefreshing}
                className="h-6 w-6 p-0"
              >
                <RefreshCwIcon
                  className={`h-3 w-3 ${isRefreshing ? 'animate-spin' : ''}`}
                />
              </Button>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" align="start" className="max-w-xs">
          <div className="space-y-2">
            <div className="font-medium">{statusConfig.description}</div>

            <div className="text-xs space-y-1">
              <div>
                <strong>Webhook ID:</strong> {webhook.id}
              </div>
              <div>
                <strong>URL:</strong> {webhook.url}
              </div>
              <div>
                <strong>Events:</strong> {webhook.events.join(', ')}
              </div>
              <div>
                <strong>Created:</strong> {formatDate(webhook.createdAt)}
              </div>
              {webhook.lastActivityAt != null &&
                webhook.lastActivityAt !== '' && (
                  <div>
                    <strong>Last Activity:</strong>{' '}
                    {formatDate(webhook.lastActivityAt)}
                  </div>
                )}
            </div>

            {statusConfig.status === 'error' && (
              <div className="text-xs text-destructive">
                Click refresh to check status again
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const WebhookStatusIndicator = ({
  webhook,
  isRefreshing = false,
  onRefresh,
  showDetails = true,
  size = 'md',
}: WebhookStatusIndicatorProps): JSX.Element => {
  const statusConfig = getStatusConfig(webhook);

  if (!showDetails || !webhook) {
    return (
      <SimpleIndicator
        statusConfig={statusConfig}
        isRefreshing={isRefreshing}
        onRefresh={onRefresh}
        size={size}
      />
    );
  }

  return (
    <DetailedIndicator
      webhook={webhook}
      statusConfig={statusConfig}
      isRefreshing={isRefreshing}
      onRefresh={onRefresh}
      size={size}
    />
  );
};
